import React, { useState } from 'react'

import update from 'react-addons-update'
import { useNavigate } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import styled from 'styled-components'

import { CartPlugin } from '@api/local/CartPlugin'
import { IconEnum, ResponsiveImage, SmallLoader, Paragraph, Spacer, Button } from '@client/components/atoms'
import { Ellipsis, ResponsivePXValue, theme, ZeroSpace } from '@client/components/Theme'
import { useConfig } from '@contexts/ConfigProvider'
import { useEvents } from '@contexts/GTMProvider'
import { CartItemFragment, useRemoveItemFromCartMutation, useChangeCartItemQuantityMutation, useUserCartQuery } from '@hooks/api'
import { ItemInterfaceNew } from '@lib/GTM'
import { Utilities } from '@lib/Utilities'
import { convertEnumToNumFRZN, Counter, CounterVariantEnum, StickyHealthyDining, StickyHealthyDiningEnum } from '@molecules/index'
import { Product, ProductAvailabilityEnum } from '@uctypes/api/globalTypes'
import { useOutOfStockNotifier } from '@utility/Commons'

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: stretch;
  max-width: 100%;
  ${ResponsivePXValue('padding', '8px 16px')}

  .text {
    ${ResponsivePXValue('width', { mobile: 'CALC(100vw - 96px)', tablet: '260px', desktop: '260px' })}
    ${Ellipsis()}
    line-height: 1.4;
  }

  .dark {
    font-weight: 500;
  }

  .error-text {
    ${ResponsivePXValue('width', { mobile: 'CALC(100vw - 140px)', tablet: '260px', desktop: '260px' })}
    ${Ellipsis()}
    line-height: 1.2;
    font-weight: 700;
  }
`

const ImageContainer = styled.div`
  position: relative;
  ${ResponsivePXValue('width', { mobile: '48px', tablet: '48px', desktop: '80px' })}
  ${ResponsivePXValue('height', { mobile: '48px', tablet: '48px', desktop: '80px' })}
  ${ResponsivePXValue('border-radius', '8px')}
  overflow: hidden;
  background-color: ${theme.colors.whites.desertStorm};
`

const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  .price-text {
    ${ZeroSpace}
    font-weight: 500;
    line-height: 1;
  }
`

const CounterContainer = styled.div`
  .counter {
    ${ResponsivePXValue('width', '96px')}
  }
`

const IconContainer = styled.div`
  ${ResponsivePXValue('width', '12px')}
  ${ResponsivePXValue('height', '12px')}

  .remove-icon-button {
    ${ZeroSpace}
  }
`

const OverlayContainer = styled.div`
  width: 100%;
  ${ResponsivePXValue('height', '96px')}
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`

const LoaderBox = styled.div`
  ${ResponsivePXValue('width', '24px')}
  ${ResponsivePXValue('height', '24px')}
`
const LinkContainer = styled.a`
  cursor: pointer;
`

export interface AddOnCartItemProps {
  item: CartItemFragment
  subtitle: string
  product: CartItemFragment['product']
  userIsOnDemand: boolean
  showVitalityTag?: boolean
}

interface AddOnCartItemState {
  loading: boolean
}

const DEFAULT_STATE: AddOnCartItemState = {
  loading: false,
}

export function AddOnCartItem({ item, subtitle, product, userIsOnDemand, showVitalityTag = false }: AddOnCartItemProps): JSX.Element {

  const [state, setState] = useState<AddOnCartItemState>({ ...DEFAULT_STATE })
  const { addToast } = useToasts()
  const navigate = useNavigate()
  const [removeItemFromCart] = useRemoveItemFromCartMutation()
  const events = useEvents()
  const config = useConfig()
  const { data: userCartData } = useUserCartQuery({ ssr: config.fetchSSRQuery() })
  const isOutOfStockItem = useOutOfStockNotifier()
  const checkoutStatus = userCartData?.currentUser?.checkoutStatus

  const [changeCartItemQuantity] = useChangeCartItemQuantityMutation()
  const _handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault()
    const type = product.__typename

    let url = ''
    switch (type) {
      case 'Wine':
        url = `/wine/${product.slug}`
        break
      case 'FrozenMeal':
        url = `/frozen/craft-meal/${product.group.slug}`
        break
      case 'MealKit':
        url = `/meal-kit/${product.group.slug}`
        break
      case 'MarketProduct':
        url = `/market/${product.slug}`
        break
    }
    navigate(url)
    CartPlugin.shared().close()
  }
  const gaAddToCart = (quantity: number): void => {
    // Analytics
    let logVariant = ''
    let category = ''
    let itemChef = 'UCOOK'
    let itemSize = 1
    let itemId
    let itemName
    let itemGroupId
    let itemstock

    switch (product.__typename) {
      case 'Wine':
        category = 'Wines'
        logVariant = product?.wineCategory?.title
        itemChef = product?.vineyard?.name
        itemId = product?.id
        itemName = product?.name
        itemGroupId = product?.id
        itemstock = product?.stockCount

        break
      case 'FrozenMeal':
        category = 'Craft Meals'
        itemName = product?.group?.name
        logVariant = product?.frozenCategories?.map((cat) => cat.title).join(', ')
        itemSize = convertEnumToNumFRZN(product?.frozenPortionSize)
        itemId = product?.id
        itemGroupId = product?.group?.id
        itemstock = product?.stockCount
        break
      case 'MarketProduct':
        category = 'Market Products'
        logVariant = product?.marketProductCategories?.map((cat) => cat.title).join(', ')
        itemId = product?.id
        itemName = product?.name
        itemGroupId = product?.id
        itemstock = product?.stockCount

        break
    }

    const logData = {
      itemName,
      itemId,
      itemGroupId,
      price: item?.price,
      itemBrand: 'UCOOK',
      itemCategory: category,
      itemVariant: logVariant,
      itemListName: 'Cart',
      quantity,
      itemImage: product?.coverImage?.location,
      itemStockCount: itemstock > 1 ? 1 : 0,
      itemChef,
      isMealkit: 'no',
      itemServingSize: itemSize,
    }

    const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew

    events.hasAddedToCart(snakedData)
  }

  const _handleQuantityChange = async (quantity: number): Promise<void> => {
    try {

      const isAddingToCart = quantity > item.quantity

      if (isAddingToCart && isOutOfStockItem(item as unknown as Product, item.quantity, checkoutStatus.subscriptionIsSaved)) {
        return
      }

      _toggleLoading(true)
      await changeCartItemQuantity({
        variables: {
          productId: item.product.id,
          quantity,
        },
      })
      if (isAddingToCart) {
        gaAddToCart(1)
      }
    } catch (e) {
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
    _toggleLoading(false)
  }

  const _handleRemove = async (): Promise<void> => {
    try {
      _toggleLoading(true)
      // const amount = item.quantity
      await removeItemFromCart({
        variables: {
          productId: item.product.id,
        },
      })
      let logVariant = ''
      let category = ''
      let itemChef = 'UCOOK'
      let itemSize = 1
      const product = item?.product

      switch (product.__typename) {
        case 'Wine':
          category = 'Wines'
          logVariant = product?.wineCategory?.title
          itemChef = product?.vineyard?.name
          break
        case 'FrozenMeal':
          category = 'Craft Meals'
          logVariant = product?.frozenCategories?.map((cat) => cat.title).join(', ')
          itemSize = convertEnumToNumFRZN(product?.frozenPortionSize)

          break
        case 'MarketProduct':
          category = 'Market Products'
          logVariant = product?.marketProductCategories?.map((cat) => cat.title).join(', ')
          break
      }

      const logData = {
        itemName: product?.name,
        itemId: product?.id,
        itemImage: product?.coverImage?.location,
        price: product?.price,
        itemBrand: 'UCOOK',
        itemListName: 'Craft Meals',
        itemChef,
        isMealkit: 'no',
        item_variant: logVariant,
        itemServingSize: itemSize,
      }

      const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew

      events.hasRemovedFromCart(snakedData)
    } catch (e) {
      addToast(e.message, {
        appearance: 'error',
        autoDismiss: true,
      })
    }
  }

  const _toggleLoading = (state: boolean): void => {
    setState((prevState) => update(prevState, {
      loading: { $set: state },
    }))
  }

  let isLowStock = false
  let isOutOfStock = false
  let maxAmount = 0
  let availableAmount = 0

  if (userIsOnDemand) {
    maxAmount = item.product.stockCount
    availableAmount = item.product.stockCount
  } else {
    maxAmount = item.product.stockCount + item.quantity
    availableAmount = item.product.stockCount + item.quantity
  }
  if (maxAmount <= 0) {
    isOutOfStock = true
  } else if (maxAmount <= item.quantity) {
    isLowStock = true
  }
  const notGeographicallyAvailable = item?.product?.availability === ProductAvailabilityEnum.UNAVAILABLE_GEOGRAPHICALLY
  const notAvailableForOrderType = item?.product?.availability === ProductAvailabilityEnum.UNAVAILABLE_FOR_ORDER_TYPE
  const notAvailable  =item?.product?.availability === ProductAvailabilityEnum.NOT_AVAILABLE

  return (
    <Container>
      <LinkContainer onClick={_handleLinkClick}>
        <ImageContainer>
          <If condition={showVitalityTag}>
            <StickyHealthyDining type={StickyHealthyDiningEnum.SMALL} />
          </If>
          <ResponsiveImage image={item.product.coverImage} />
        </ImageContainer>
      </LinkContainer>
      <Spacer universal='8px' variant='horizontal' />
      <BodyContainer>
        <Spacer universal='4px' />
        <LinkContainer onClick={_handleLinkClick}>
          <Paragraph variant='p2' className='text dark'>{item.product.name.split(' - ')[0]}</Paragraph>
          <Spacer universal='12px' />
          <Choose>
            <When condition={notGeographicallyAvailable}>
              <Paragraph variant='p3' color={theme.colors.misc.error} className='error-text'>{'NOT GEOGRAPHICALLY AVAILABLE'}</Paragraph>
            </When>
            <When condition={isOutOfStock}>
              <Paragraph variant='p3' color={theme.colors.misc.error} className='error-text'>{'NO STOCK'}</Paragraph>
            </When>
            <When condition={notAvailable}>
              <Paragraph variant='p3' color={theme.colors.misc.error} className='error-text'>{'PRODUCT IS NO LONGER AVAILABLE'}</Paragraph>
            </When>
            <When condition={isLowStock}>
              <Paragraph variant='p3' color={theme.colors.misc.error} className='error-text'>{`ONLY ${availableAmount} AVAILABLE`}</Paragraph>
            </When>            
            <Otherwise>
              <Paragraph variant='p3' className='text'>{subtitle}</Paragraph>
            </Otherwise>
          </Choose>
        </LinkContainer>
        <Spacer universal='8px' />
        <FooterContainer>
          <CounterContainer>
            <Counter variant={CounterVariantEnum.SMALL} className='counter' minimum={1} amount={item.quantity} onChange={_handleQuantityChange} />
          </CounterContainer>
          <Paragraph variant='p2' className='price-text'>{`R${item.price}`}</Paragraph>
        </FooterContainer>
      </BodyContainer>

      <Spacer universal='8px' />
      <IconContainer>
        <Button
          color='transparent'
          icon={IconEnum.CLOSE_OUTLINE}
          iconColor={theme.colors.greys.darkCodGrey}
          size='small'
          onClick={_handleRemove} />
      </IconContainer>
      <If condition={state.loading}>
        <OverlayContainer>
          <LoaderBox>
            <SmallLoader color={theme.colors.oranges.coral} />
          </LoaderBox>
        </OverlayContainer>
      </If>
    </Container>
  )
}
